import { useEffect, useRef, type CSSProperties } from "react";
import { Keycap } from "@/components/keycap/Keycap";
import type { KeycapId } from "@/components/keycap/keycapCatalog";
import { prefersReducedMotion, isCoarsePointer } from "@/utils/motion";
import "./HeroCanvas.css";

interface PadKey {
  id: KeycapId;
  label: string;
}

/**
 * The one hero graphic: a small macropad, not a diagram of the platform.
 * Four keys — Compose, Connect, Qualify, Publish — lit in sequence on a
 * circuit-board plate. Everything else on the homepage is supporting
 * evidence for this one idea: engineering software is composed, not
 * written. See the 2026-07-17 keycap art-direction pass.
 */
const keys: PadKey[] = [
  { id: "compose", label: "Compose" },
  { id: "connect", label: "Connect" },
  { id: "qualify", label: "Qualify" },
  { id: "publish", label: "Publish" },
];

export function HeroCanvas() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === "undefined") return;

    el.classList.add("hero-canvas-pending");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add("hero-canvas-playing");
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || isCoarsePointer()) return;

    let frame = 0;
    const handleMove = (event: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const box = el.getBoundingClientRect();
        const px = (event.clientX - box.left) / box.width - 0.5;
        const py = (event.clientY - box.top) / box.height - 0.5;
        el.style.setProperty("--tilt-x", `${(-py * 4).toFixed(2)}deg`);
        el.style.setProperty("--tilt-y", `${(px * 4).toFixed(2)}deg`);
      });
    };
    const reset = () => {
      el.style.setProperty("--tilt-x", "0deg");
      el.style.setProperty("--tilt-y", "0deg");
    };

    el.addEventListener("pointermove", handleMove);
    el.addEventListener("pointerleave", reset);
    return () => {
      el.removeEventListener("pointermove", handleMove);
      el.removeEventListener("pointerleave", reset);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="hero-canvas"
      role="img"
      aria-label="A small macropad with four keys, Compose, Connect, Qualify and Publish, representing the Devory composition model."
    >
      <div className="hero-canvas-stage">
        <div className="hero-canvas-plate">
          <ol className="hero-canvas-keys">
            {keys.map((key, index) => (
              <li
                key={key.id}
                className="hero-canvas-key"
                style={{ "--i": index } as CSSProperties}
              >
                <Keycap id={key.id} size="lg" label={key.label} />
              </li>
            ))}
          </ol>
        </div>
      </div>
      <p className="hero-canvas-caption">Composed, not written.</p>
    </div>
  );
}
